import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import {
  AcceptOrderFoodOrderPayload,
  AckOrderFoodCommand,
  GetMyOrdersParams,
  NeedForCallOrderPayload,
  PickOrderPayload,
} from '../models/payload/order';
import { MyOrdersResponse, OrderByIdResponse } from '../models/response/order';
import {
  acceptFoodOrderService,
  ackFoodOrderService,
  getMyOrder,
  getOrder,
  needCallService,
  setPickToFoodOrder,
} from '../services/order';
import { createAppAsyncThunk } from '../utils/reduxUtil';

export interface OrderState {
  orders: MyOrdersResponse | undefined;
  order: OrderByIdResponse | undefined;
  loading: boolean;
}

const initialState: OrderState = {
  orders: undefined,
  order: undefined,
  loading: false,
};

const getAllOrders = createAppAsyncThunk<MyOrdersResponse, GetMyOrdersParams>(
  'order/getAllMyOrders',
  async (params: GetMyOrdersParams) => {
    const response = await getMyOrder(params);
    return response;
  }
);

const getOrderById = createAppAsyncThunk<OrderByIdResponse, { id: string; networkStatus: boolean }>(
  'order/getOrder',
  async (input) => {
    const response = await getOrder({
      id: input.id,
      networkStatus: input.networkStatus,
    });
    return response;
  }
);

const acceptFoodOrder = createAppAsyncThunk<void, AcceptOrderFoodOrderPayload>(
  'order/acceptFoodOrder',
  async (input) => {
    const response = await acceptFoodOrderService(input);
    return response;
  }
);

const rejectReasonSnappFood = createAppAsyncThunk<void, NeedForCallOrderPayload>(
  'order/rejectReasonSnappFood',
  async (input) => {
    const response = await needCallService(input);
    return response;
  }
);

const ackFoodOrder = createAppAsyncThunk<void, AckOrderFoodCommand>('order/ackFoodOrder', async (input) => {
  const response = await ackFoodOrderService(input);
  return response;
});

const setPickToOrder = createAppAsyncThunk<void, PickOrderPayload>('order/setPick', async (command) => {
  const response = await setPickToFoodOrder(command);
  return response;
});

function removeDuplicate<T, K extends keyof T>(source: T[], destination: T[], compareFiled: K): T[] {
  const ids = new Set(source.map((d) => d[compareFiled]));
  return [...source, ...destination.filter((d) => !ids.has(d[compareFiled]))];
}

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    clearOrder: (state) => ({
      ...state,
      order: undefined,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<OrderState>) => {
    builder.addCase(getAllOrders.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(getAllOrders.fulfilled, (state, action) => ({
      ...state,
      orders: {
        ...action?.payload,
        data: {
          ...action?.payload?.data,
          items: removeDuplicate(state.orders?.data?.items || [], action?.payload?.data?.items || [], 'id'),
        },
      },
      loading: false,
    }));
    builder.addCase(getOrderById.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(getOrderById.fulfilled, (state, action) => ({
      ...state,
      order: action?.payload,
      loading: false,
    }));
    builder.addCase(getOrderById.rejected, (state) => ({ ...state, loading: false }));
    builder.addCase(setPickToOrder.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(setPickToOrder.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(setPickToOrder.rejected, (state) => ({ ...state, loading: false }));
    builder.addCase(acceptFoodOrder.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(acceptFoodOrder.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(acceptFoodOrder.rejected, (state) => ({ ...state, loading: false }));

    builder.addCase(rejectReasonSnappFood.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(rejectReasonSnappFood.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(rejectReasonSnappFood.rejected, (state) => ({ ...state, loading: false }));
  },
});

export default orderSlice;
export { acceptFoodOrder, ackFoodOrder, getAllOrders, getOrderById, rejectReasonSnappFood, setPickToOrder };
